import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { ArrivalVM } from '../../../../../models/ArrivalDepartureModel';
import { ArrivalDepartureService } from '../../../../../services/arrivaldeparture.service';
import { DatePipe } from '@angular/common';
import { ArrivalDepartureDetailsComponent } from '../arrivaldeparture-details/arrivaldeparture-details.component';
import { ArrivalNewComponent } from '../arrival-new/arrival-new.component';
import { BackOffice24StatesService } from '../../state/back-office24-states.service';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { Subscription } from 'rxjs';
import { MyConfigService } from '../../../../../services/my-config.service';

@Component({
  selector: 'app-arrival-list',
  templateUrl: './arrival-list.component.html',
  styleUrls: ['./arrival-list.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ArrivalListComponent implements OnInit, OnDestroy {
  @Input() organization: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();
  dateFrom: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
  arrivalForm: UntypedFormGroup;
  arrivals: ArrivalVM[];
  dataSource: MatTableDataSource<ArrivalVM> = new MatTableDataSource<ArrivalVM>([]);
  @Output() incrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Output() decrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Input() isLoading: boolean = false;

  // New filter properties
  filtersExpanded: boolean = true;
  availableStatuses: string[] = [
    "Landed",
    "Scheduled",
    "Deleted",
    "Active",
    "New"
  ];
  arrivalsLastUpdated: Date;

  displayedColumns: string[] = [
    'stationName',
    'terminalName',
    'arrivalCode',
    'operator',
    'scheduledArrival',
    'estimatedArrival',
    'actualArrival',
    'flightDepartureDate',
    'status',
    'remarks',
    'departureStation',
    'gate',
    'firstBag',
    'lastBag',
    'baggageClaimUnit',
    'updated',
    'orderShuttles'
  ];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  componentDestroyed$: Subject<boolean> = new Subject();

  constructor(
    public arrivalDepartureService: ArrivalDepartureService,
    public dialog: MatDialog,
    public router: Router,
    private fb: UntypedFormBuilder,
    public datepipe: DatePipe,
    public backOffice24StatesService: BackOffice24StatesService,
    public myConfigService: MyConfigService
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => this.organization = org
    );
    this.initArrivals();
  }

  initArrivals() {
    this.dateFrom = moment().format();
    this.timeFrom = "00:00";
    this.dateTo = moment().add(2, 'd').format();
    this.timeTo = "00:00";
    this.initArrivalForm();
    this.getArrivals();
  }

  initArrivalForm() {
    this.arrivalForm = this.fb.group({
      dateFrom: [this.dateFrom],
      timeFrom: [this.timeFrom],
      dateTo: [this.dateTo],
      timeTo: [this.timeTo],
      statusFilter: [[]],  // Array for multiple selection
      shuttleFilter: ['withShuttles']  // Default to show all flights
    });

    // Subscribe to form changes to apply filters automatically
    this.arrivalForm.valueChanges.pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe(() => {
      this.applyAdvancedFilters();
    });
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  clearFilters(): void {
    this.arrivalForm.patchValue({
      statusFilter: [],
      shuttleFilter: 'all'
    });
    this.applyAdvancedFilters();
  }

  applyAdvancedFilters(): void {
    if (!this.arrivals) return;

    let filteredData = [...this.arrivals];
    const formValues = this.arrivalForm.value;

    // Filter by status
    if (formValues.statusFilter && formValues.statusFilter.length > 0) {
      filteredData = filteredData.filter(arrival =>
        formValues.statusFilter.includes(arrival.status)
      );
    }

    // Filter by shuttle orders
    if (formValues.shuttleFilter === 'withShuttles') {
      filteredData = filteredData.filter(arrival =>
        arrival.orderShuttles && arrival.orderShuttles > 0
      );
    } else if (formValues.shuttleFilter === 'withoutShuttles') {
      filteredData = filteredData.filter(arrival =>
        !arrival.orderShuttles || arrival.orderShuttles === 0
      );
    }

    this.dataSource.data = filteredData;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  getArrivals() {
    this.incrementRequests.emit();
    this.setDateFormats();
    this.arrivals = [];
    this.arrivalDepartureService
      .getArrivalsStationBetweenArrivalDate(this.organization.config.stations[0], this.dateFrom, this.dateTo)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (arrivals) => {
          this.arrivals = arrivals;
          this.arrivalsLastUpdated = new Date(); // Track when data was last updated
          this.dataSource = new MatTableDataSource<ArrivalVM>(this.arrivals);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.applyAdvancedFilters(); // Apply filters after loading data
          this.decrementRequests.emit();
        },
        error: (error) => {
          this.decrementRequests.emit();
          console.error('ArrivalListComponent - Error in getArrivals', error);
        }
      });
  }

  showSelected(arrival: ArrivalVM): void {
    this.dialog.open(ArrivalDepartureDetailsComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw',
      hasBackdrop: true,
      data: {
        ...arrival,
        arrivalId: arrival.arrivalId,
        departureId: null,
        isShuttles: arrival.orderShuttles
      }
    });
  }

  createArrival(): void {
    this.dialog.open(ArrivalNewComponent, {
      width: '720px',
      hasBackdrop: true
    });
  }

  setDateFormats() {
    this.dateFrom = this.datepipe.transform(this.arrivalForm.controls['dateFrom'].value, 'yyyy-MM-dd') + ' ' + this.arrivalForm.controls['timeFrom'].value;
    this.dateTo = this.datepipe.transform(this.arrivalForm.controls['dateTo'].value, 'yyyy-MM-dd') + ' ' + this.arrivalForm.controls['timeTo'].value;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  flatten(obj: any) {
    return Object.keys(obj).reduce((acc, current) => {
      const _key = `${current}`;
      const currentValue = obj[current];
      if (Array.isArray(currentValue) || Object(currentValue) === currentValue) {
        Object.assign(acc, this.flatten(currentValue));
      } else {
        acc[_key] = currentValue;
      }
      return acc;
    }, {});
  }

  ngOnDestroy() {
    this.orgSubscription.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
